import { CartCompletenessStatus, CaseBuildStatus } from '@peritasai/types';
import { BuildChecklistEntity } from '../build-cart/build-checklist.entity';

type TrayAvailability = {
  catalogItemId: string;
  quantity: number;
  availableCount: number;
};

/**
 * Determines whether any tray is unavailable based on remaining needed vs remaining available.
 */
export function hasUnavailableTrays(
  trays?: TrayAvailability[],
  buildChecklistEntity?: BuildChecklistEntity
): boolean {
  if (!trays || trays.length === 0) {
    return false;
  }
  // If the checklist already has all trays allocated, no need to flag unavailable
  if (
    buildChecklistEntity &&
    buildChecklistEntity.traysAllocated === buildChecklistEntity.traysTotal
  ) {
    return false;
  }

  return trays.some((tray) => {
    if (buildChecklistEntity) {
      const checklistTray = buildChecklistEntity
        .getTrays()
        .find((t) => t.catalogItemId === tray.catalogItemId);
      const allocated = checklistTray?.allocatedCount ?? 0;
      const remainingNeeded = tray.quantity - allocated;
      const remainingAvailable = tray.availableCount - allocated;
      return remainingNeeded > remainingAvailable;
    }

    return tray.quantity > tray.availableCount;
  });
}

/**
 * Computes the build status using tray availability and allocation counts.
 */
export function computeBuildStatus(
  buildChecklist?: BuildChecklistEntity,
  trays?: TrayAvailability[]
): CaseBuildStatus | null {
  if (!buildChecklist) {
    return null;
  }

  const { traysAllocated, traysTotal } = buildChecklist;

  if (hasUnavailableTrays(trays, buildChecklist)) {
    return CaseBuildStatus.BLOCKED;
  }

  if (traysTotal === traysAllocated) {
    return CaseBuildStatus.COMPLETE;
  }

  return CaseBuildStatus.INCOMPLETE;
}

/**
 * Map CaseBuildStatus enum to CartCompletenessStatus string based on business rules
 * Priority order:
 * 1. Dispatched statuses (check dispatchStatus first)
 * 2. BLOCKED status (trays unavailable)
 * 3. Ready for Dispatch (all trays allocated)
 * 4. Build status-based statuses (IN_PROGRESS, INCOMPLETE, NOT_STARTED)
 */
export function mapBuildStatusToCartCompletenessStatus(
  dispatchStatus: boolean,
  buildChecklist?: {
    buildStatus: CaseBuildStatus;
    traysAllocated: number;
    traysTotal: number;
  },
  trays?: Array<{
    catalogItemId: string;
    quantity: number;
    availableCount: number;
  }>,
  buildChecklistEntity?: BuildChecklistEntity,
  isBuildHeader = false
): CartCompletenessStatus {
  // If no buildChecklist, return 'Not Started'
  if (!buildChecklist) {
    return 'Not Started';
  }

  const { buildStatus, traysAllocated, traysTotal } = buildChecklist;

  // Rule 1: Dispatched - if dispatchStatus = true and buildStatus = COMPLETE
  if (dispatchStatus === true) {
    if (buildStatus === CaseBuildStatus.COMPLETE) {
      return 'Dispatched';
    }
    // Rule 2: Dispatched - Incomplete - if dispatchStatus = true and buildStatus != COMPLETE
    return 'Dispatched - Incomplete';
  }

  if (isBuildHeader) {
    // Rule 4: Trays Unavailable - check if any tray has remaining needed > remaining available
    // Account for trays already allocated to this case
    const traysUnavailable = hasUnavailableTrays(trays, buildChecklistEntity);
    if (traysUnavailable) {
      return 'Trays Unavailable';
    }
  } else {
    // Fallback: if buildStatus = BLOCKED, also return Trays Unavailable
    if (buildStatus === CaseBuildStatus.IN_PROGRESS) {
      return 'In Progress';
    } else {
      // Rule 4: Trays Unavailable - check if any tray has remaining needed > remaining available
      // Account for trays already allocated to this case
      const traysUnavailable = hasUnavailableTrays(trays, buildChecklistEntity);
      if (traysUnavailable) {
        return 'Trays Unavailable';
      }
    }
  }

  // Rule 3: Ready for Dispatch - if all trays are checked (traysAllocated == traysTotal)
  // OR if buildStatus = COMPLETE (build is complete, ready to dispatch)
  if (
    (traysTotal > 0 && traysAllocated === traysTotal) ||
    buildStatus === CaseBuildStatus.COMPLETE
  ) {
    return 'Ready for Dispatch';
  }

  // Rule 5: Incomplete - if user has selected some trays but not all
  // OR if cart is selected but no trays allocated yet
  // (traysTotal > traysAllocated means user started but didn't finish)
  if (
    (traysTotal > 0 && traysAllocated > 0 && traysAllocated < traysTotal) ||
    (buildChecklistEntity?.hasCartSelected() &&
      traysTotal > 0 &&
      traysAllocated === 0)
  ) {
    return 'Incomplete';
  }

  // Rule 6: Not Started - if buildStatus = NOT_STARTED
  if (buildStatus === CaseBuildStatus.NOT_STARTED) {
    return 'Not Started';
  }

  // Default fallback
  return 'Not Started';
}
